import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { Geolocation } from '@capacitor/geolocation';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';

class MobileService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform();
    this.init();
  }

  async init() {
    if (this.isNative) {
      await this.setupStatusBar();
      await this.setupSplashScreen();
      await this.setupKeyboard();
    }
  }

  // Status Bar Configuration
  async setupStatusBar() {
    try {
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#F59E0B' });
    } catch (error) {
      console.log('StatusBar setup error:', error);
    }
  }

  // Splash Screen Management
  async setupSplashScreen() {
    try {
      // Hide splash screen after app is loaded
      setTimeout(async () => {
        await SplashScreen.hide();
      }, 2000);
    } catch (error) {
      console.log('SplashScreen setup error:', error);
    }
  }

  // Keyboard Management
  async setupKeyboard() {
    try {
      // Listen to keyboard events
      Keyboard.addListener('keyboardWillShow', (info) => {
        console.log('Keyboard will show with height:', info.keyboardHeight);
      });

      Keyboard.addListener('keyboardDidHide', () => {
        console.log('Keyboard did hide');
      });
    } catch (error) {
      console.log('Keyboard setup error:', error);
    }
  }

  // Device Information
  async getDeviceInfo() {
    try {
      const deviceInfo = await Device.getInfo();
      console.log('Device Info:', deviceInfo);
      return deviceInfo;
    } catch (error) {
      console.log('Device info error:', error);
      return null;
    }
  }

  // Network Status
  async getNetworkStatus() {
    try {
      const status = await Network.getStatus();
      return status;
    } catch (error) {
      console.log('Network status error:', error);
      return { connected: true, connectionType: 'unknown' };
    }
  }

  // Listen to network changes
  setupNetworkListener(callback) {
    if (this.isNative) {
      Network.addListener('networkStatusChange', callback);
    }
  }

  // Preferences (Local Storage for Native)
  async setPreference(key, value) {
    try {
      await Preferences.set({ key, value: JSON.stringify(value) });
    } catch (error) {
      console.log('Set preference error:', error);
      // Fallback to localStorage
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  async getPreference(key) {
    try {
      const result = await Preferences.get({ key });
      return result.value ? JSON.parse(result.value) : null;
    } catch (error) {
      console.log('Get preference error:', error);
      // Fallback to localStorage
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    }
  }

  async removePreference(key) {
    try {
      await Preferences.remove({ key });
    } catch (error) {
      console.log('Remove preference error:', error);
      localStorage.removeItem(key);
    }
  }

  // Geolocation
  async getCurrentPosition() {
    try {
      const coordinates = await Geolocation.getCurrentPosition();
      return {
        latitude: coordinates.coords.latitude,
        longitude: coordinates.coords.longitude,
        accuracy: coordinates.coords.accuracy
      };
    } catch (error) {
      console.log('Geolocation error:', error);
      return null;
    }
  }

  async requestLocationPermissions() {
    try {
      const permissions = await Geolocation.requestPermissions();
      return permissions.location === 'granted';
    } catch (error) {
      console.log('Location permission error:', error);
      return false;
    }
  }

  // Platform Detection
  isAndroid() {
    return this.platform === 'android';
  }

  isIOS() {
    return this.platform === 'ios';
  }

  isWeb() {
    return this.platform === 'web';
  }

  isMobile() {
    return this.isAndroid() || this.isIOS();
  }

  // Utility Methods
  async vibrate(pattern = [100]) {
    if (this.isNative && 'vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }

  async shareContent(title, text, url) {
    if (navigator.share && this.isMobile()) {
      try {
        await navigator.share({ title, text, url });
        return true;
      } catch (error) {
        console.log('Share error:', error);
        return false;
      }
    }
    return false;
  }

  // App State Management
  setupAppStateListeners() {
    if (this.isNative) {
      document.addEventListener('resume', () => {
        console.log('App resumed');
        // Refresh app data when resuming
        window.dispatchEvent(new CustomEvent('app-resume'));
      });

      document.addEventListener('pause', () => {
        console.log('App paused');
        // Save app state when pausing
        window.dispatchEvent(new CustomEvent('app-pause'));
      });
    }
  }
}

export default new MobileService();